/* import __COLOCATED_TEMPLATE__ from './lsg-bubble-text-editor.hbs'; */
import { scheduleOnce } from '@ember/runloop';
import { computed, observer } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { showModal, closeModal } from "sh-www/utils/modal-controls";
import AssetUploadManager from '../../mixins/asset-upload-manager';

export default Component.extend(AssetUploadManager, {
  session: service(),
  currentUser: readOnly('session.currentUser'),

  /**
   * HTML Component properties
   */
  tagName: 'div',
  classNames: ['LSG-c-bubble-text-editor'],
  classNameBindings: ['isSavingText:LSG-c-bubble-text-editor--saving'],

  /**
   * Register services
   */

  /**************************************************************
   *                  REQUIRED PARAMS
   **************************************************************/

  /**
   * @type {Boolean}      True if we are in edit mode.
   */
  isInEditMode: false,

  /**
   * @param {Function}
   */
  onCancelClicked: function() {},

  /**
   * @param {Function}
   */
  onSaveClicked: function(noteValue) {
    throw new Error('define on save clicked!');
  },

  /**
   * @param {String}    The value of the text as input by the user.  Passed by reference.
   */
  value: '',

  /**************************************************************
   *                  OPTIONAL PARAMS
   **************************************************************/

  /**
   * @param {String=}      The text for the action button with the editor.
   */
  actionButtonText: 'Save',

  /**
   * @param {Boolean} attachmentsEnabled Whether files may be attached to the message.
   */
  attachmentsEnabled: false,

  /**
   * @param {Boolean} hasUploads Whether the user has added uploads.
   */
  hasUploads: null,

  setHasUploads: () => {},

  /**
   * @type {String=}      Optional. The URI for the src to show while in edit mode.
   *                      Will default to the thumb loaded for current user.
   */
  imgSrcForEditing: '',

  /**
   * @param {Boolean=}    True when we're saving a new text.
   */
  isSavingText: false,

  /**
   * @param {String=}     Place holder text to show when in non-edit mode.
   */
  placeholder: '...',

  inputMaxLength: undefined,

  /**************************************************************
   *                  PRIVATE
   **************************************************************/

  inputLength: computed('value', function() {
    return this.value.length;
  }),

  isShowingInputLengthWarning: computed('inputMaxLength', 'inputLength', function() {
    return this.inputLength >= this.inputMaxLength;
  }),

  characterLimitClasses: computed('isShowingInputLengthWarning', function() {
    return this.isShowingInputLengthWarning ? 'lsg-red' : 'lsg-light-gray';
  }),

  _imgSrc: computed('imgSrcForEditing', function() {
    let imgSrc = this.get('imgSrcForEditing');
    if (imgSrc) {
      return imgSrc;
    }

    return this.get('currentUser.image.transforms.profile128');
  }),

  /**
   * @type {Boolean} Whether the save button is disabled.
   */
  _isSaveButtonDisabled: computed('isUploadingAssets', 'value', function () {
    return this.isUploadingAssets || this.value === '';
  }),

  /**
   * Watches the upload list and sets the hasUploads flag accordingly.
   */
  observeUploadList: observer('uploadList.[]', function () {
    this.setHasUploads?.(this.uploadList.length > 0);
  }),

  actions: {
    /**
     * Called when user clicks to edit text.
     */
    onAddTextClicked(e) {
      this.onAddTextClicked?.(e);

      if (e.defaultPrevented) {
        return;
      }

      this.set('isInEditMode', true);

      scheduleOnce('afterRender', this, function() {
        this.element.querySelector('textarea').focus();
      });
    },

    /**
     * Called when user clicks the cancel action
     */
    onCancelClicked: function() {
      this.set('isInEditMode', false);
      this.set('value', '');
      this.resetAssetUploadManager();
      this.get('onCancelClicked')();
    },

    /**
     * Called when user clicks the save action
     */
    onSaveClicked: function() {
      this.set('isInEditMode', false);

      const assetIDs = [];
      this.get('uploadCompletedList').forEach((uploadID) => {
        const assetID = this.get(`uploads.${uploadID}.assetID`);
        assetIDs.push(assetID);
      });

      this.get('onSaveClicked')(this.get('value'), assetIDs)
        .then(() => {
          if (!this.isDestroying) {
            this.set('value', '');
            this.resetAssetUploadManager();
          }
        });
    },

    /**
     * Called when the file input component receives new files.
     */
    onFileInputChange: function(files) {
      this.addFiles(files);
    },

    /**
     * Called when an asset's delete button is clicked.
     */
    onClickDeleteAssetButton: function(uploadID) {
      this.removeFile(uploadID);
    },

    /**
     * Called when the user clicks the saved messages action
     *
     * @param {MouseEvent} e
     */
    showSavedMessagesModal(e) {
      e.preventDefault();

      showModal(this, 'saved-messages', {
        onMessageSelect: (message) => {
          closeModal(this);

          const currentValue = this.get('value') || '';
          const newValue = currentValue + (currentValue ? '\n' : '') + message.get('body');

          this.set('value', newValue);

          scheduleOnce('afterRender', this, () => {
            const textarea = e.target.closest('.LSG-c-bubble-text-editor').querySelector('textarea');
            textarea.focus();
          });
        },
      });
    },
  },
});
